import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

import { db } from './firebase';

/**
 * Which physical key plays which valve.
 *
 * The answer dictionaries (brassDict, scalesDict) name every valve by its
 * original default key: 'j' 'k' 'l' for piston valves, 'q' 'w' 'e' 'c' for the
 * horn, ';' for the tuba's 4th, 'f' for the euphonium trigger and '1'-'7' for
 * trombone positions. Those names never change. A binding only decides which
 * key the player presses to play one of them, so nothing else needs to know.
 */

// Valves in the order the Options screen lists them
export const VALVES = {
  trumpet: ['j', 'k', 'l'],
  frenchHorn: ['e', 'w', 'q', 'c'],
  trombone: ['1', '2', '3', '4', '5', '6', '7'],
  euphonium: ['j', 'k', 'l', 'f'],
  tuba: ['j', 'k', 'l', ';'],
};

const HORN_NAMES = { e: 'Valve 1', w: 'Valve 2', q: 'Valve 3', c: 'Trigger' };
const PISTON_NAMES = {
  j: 'Valve 1',
  k: 'Valve 2',
  l: 'Valve 3',
  ';': '4th valve',
  f: '4th valve (trigger)',
};

export const valveLabel = (instrument, valve) => {
  if (instrument === 'trombone') return `Position ${valve}`;
  if (instrument === 'frenchHorn') return HORN_NAMES[valve] || valve;
  return PISTON_NAMES[valve] || valve;
};

const STORAGE_KEY = 'brassflows_key_bindings_v1';

// Keys that always keep their job (Space starts and answers)
const isBindable = (key) => typeof key === 'string' && key.length === 1 && key !== ' ';

/** The key to compare against bindings: single characters are case-insensitive. */
export const normalizeKey = (key) =>
  typeof key === 'string' && key.length === 1 ? key.toLowerCase() : key;

/* ---------- storage ---------- */

let cache = null; // { [instrument]: { [valve]: key } }, only the changed valves
const listeners = new Set();

const resolve = (instrument, overrides) => {
  const result = {};
  (VALVES[instrument] || []).forEach((valve) => {
    result[valve] = overrides && overrides[valve] ? overrides[valve] : valve;
  });
  return result;
};

const hasDuplicates = (map) => new Set(Object.values(map)).size !== Object.values(map).length;

// Drops anything unusable (unknown valves, bad keys, two valves on one key)
const sanitize = (raw) => {
  const clean = {};
  if (!raw || typeof raw !== 'object') return clean;
  Object.keys(VALVES).forEach((instrument) => {
    const source = raw[instrument];
    if (!source || typeof source !== 'object') return;
    const overrides = {};
    VALVES[instrument].forEach((valve) => {
      const key = normalizeKey(source[valve]);
      if (isBindable(key) && key !== valve) overrides[valve] = key;
    });
    if (Object.keys(overrides).length > 0 && !hasDuplicates(resolve(instrument, overrides))) {
      clean[instrument] = overrides;
    }
  });
  return clean;
};

const read = () => {
  if (!cache) {
    try {
      cache = sanitize(JSON.parse(localStorage.getItem(STORAGE_KEY)));
    } catch {
      cache = {};
    }
  }
  return cache;
};

let pushTimer = null;
const pushToCloud = () => {
  clearTimeout(pushTimer);
  pushTimer = setTimeout(async () => {
    try {
      const user = getAuth().currentUser;
      if (user) await updateDoc(doc(db, 'users', user.uid), { keyBindings: read() });
    } catch (error) {
      console.error('Could not save key bindings', error);
    }
  }, 800);
};

const commit = (next, { sync = true } = {}) => {
  cache = sanitize(next);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // storage unavailable; the keys just won't persist on this device
  }
  listeners.forEach((listener) => listener());
  if (sync) pushToCloud();
};

/* ---------- reading ---------- */

/** { valve: key } for every valve of the instrument. */
export const getBindings = (instrument) => resolve(instrument, read()[instrument]);

export const getBinding = (instrument, valve) => getBindings(instrument)[valve] || valve;

/** The valve a pressed key plays on this instrument, or null. */
export const keyToValve = (instrument, key) => {
  const bindings = getBindings(instrument);
  const found = Object.keys(bindings).find((valve) => bindings[valve] === key);
  return found || null;
};

export const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/* ---------- changing ---------- */

/**
 * Binds `key` to `valve`. If another valve already used that key the two swap,
 * so the player never ends up with a valve that has no key.
 * Returns { ok: true, swapped?: valveName } or { ok: false, message }.
 */
export const setBinding = (instrument, valve, rawKey) => {
  const key = normalizeKey(rawKey);
  if (key === ' ') {
    return { ok: false, message: 'Space is used to start and answer. Pick another key.' };
  }
  if (!isBindable(key)) {
    return { ok: false, message: 'Use a letter, number or symbol key.' };
  }

  const current = getBindings(instrument);
  const other = Object.keys(current).find((v) => v !== valve && current[v] === key);
  const next = { ...current, [valve]: key };
  if (other) next[other] = current[valve];

  const overrides = {};
  Object.keys(next).forEach((v) => {
    if (next[v] !== v) overrides[v] = next[v];
  });
  commit({ ...read(), [instrument]: overrides });
  return other ? { ok: true, swapped: other } : { ok: true };
};

export const resetBindings = (instrument) => {
  const next = { ...read() };
  delete next[instrument];
  commit(next);
};

/* ---------- account sync ---------- */

let syncStarted = false;

/**
 * Keeps bindings with the player's account. When someone signs in, the saved
 * set on their account wins; if they have none yet, this device's keys are
 * uploaded. Safe to call from several places.
 */
export const startKeyBindingSync = () => {
  if (syncStarted) return;
  syncStarted = true;
  onAuthStateChanged(getAuth(), async (user) => {
    if (!user) return;
    try {
      const snap = await getDoc(doc(db, 'users', user.uid));
      const cloud = snap.data() && snap.data().keyBindings;
      if (cloud && typeof cloud === 'object') {
        commit(cloud, { sync: false });
      } else if (Object.keys(read()).length > 0) {
        pushToCloud();
      }
    } catch (error) {
      console.error('Could not load key bindings', error);
    }
  });
};